// feature/eva-litisoft — the unified "Eva Litisoft" cockpit. One admin-only hub
// (replacing the five scattered Bot-LITISOFT pages) with a persona header, a
// live status line, and tabs. Operator-first: Resumen is the pilot's home; the
// safe (dry-run) path is always primary and the real upload is a deliberate,
// confirmed action. Athena-native — same tokens, .slide-tabs, .tab-panel,
// Skeleton and motion as the rest of the app. Eva just talks to you.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import EvaMark from "./EvaMark";
import { usePulse } from "./usePulse";
import ResumenTab from "./ResumenTab";
import BotLitisoftReconciliacion from "../BotLitisoftReconciliacion";
import BotLitisoftCola from "../BotLitisoftCola";
import BotLitisoftMonitor from "../BotLitisoftMonitor";
import BotLitisoftHistorial from "../BotLitisoftHistorial";
import BotLitisoftConfig from "../BotLitisoftConfig";

const TABS = [
  { id: "resumen",        label: "Resumen" },
  { id: "reconciliacion", label: "Reconciliación" },
  { id: "cola",           label: "Cola de carga" },
  { id: "monitor",        label: "Monitor" },
  { id: "historial",      label: "Historial" },
  { id: "config",         label: "Configuración" },
];
const VALID = new Set(TABS.map((t) => t.id));

// Eva's one-line status, in her voice — the operator's at-a-glance read.
function statusLine(pulse) {
  if (!pulse) return "Revisando el estado…";
  if (pulse.activeRun) {
    const r = pulse.activeRun;
    const verbo = r.dry_run ? "Simulando" : "Radicando";
    return `${verbo} la corrida #${r.id} — ${r.completed_items}/${r.total_items} casos.`;
  }
  if (!pulse.vaultConfigured) return "Aún no puedo trabajar: falta configurar la bóveda de credenciales en el servidor.";
  if (!pulse.credsReady) return "Casi lista — me faltan las credenciales de PREVISORA para poder radicar.";
  if (!pulse.worker?.paired) return "Lista para emparejar un worker. Descárgalo en Configuración y lo activamos.";
  if (pulse.eligible > 0) return `Tengo ${pulse.eligible} caso${pulse.eligible === 1 ? "" : "s"} listo${pulse.eligible === 1 ? "" : "s"} para revisar contigo.`;
  return "Todo al día — no hay casos pendientes por radicar ahora mismo.";
}

export default function EvaLitisoft() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab");
  const [tab, setTab] = useState(VALID.has(initial) ? initial : "resumen");
  const { pulse, loading, reload } = usePulse();

  // Keep ?tab in the URL so deep links + the old-path redirects land on the
  // right tab, without a full route per tab.
  useEffect(() => {
    const cur = params.get("tab");
    if (cur !== tab) { const n = new URLSearchParams(params); n.set("tab", tab); setParams(n, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="col" style={{ gap: 16 }}>
      {/* ── Eva header — persona + live status. Athena-native card. ── */}
      <div className="card" style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 20px" }}>
        <EvaMark size={40} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <h1 style={{ margin: 0, fontSize: 20 }}>Eva</h1>
            <span className="muted" style={{ fontSize: 13 }}>· tu asistente de radicación en LITISOFT</span>
          </div>
          <div style={{ marginTop: 2, fontSize: 13.5, color: pulse?.activeRun ? "var(--accent-mint)" : "var(--text-body)" }}>
            {loading && !pulse ? <span className="skeleton" style={{ width: 280, height: 14 }} /> : statusLine(pulse)}
          </div>
        </div>
        {/* compact readiness chips */}
        {pulse && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <ReadyChip ok={pulse.credsReady} label="Credenciales" />
            <ReadyChip ok={pulse.worker?.live} warn={pulse.worker?.paired && !pulse.worker?.live} label="Worker" />
          </div>
        )}
      </div>

      {/* ── Tabs — same primitive as case detail ── */}
      <div className="slide-tabs" role="tablist">
        {TABS.map((t) => (
          <div key={t.id} role="tab" aria-selected={tab === t.id}
               className={"slide-tab" + (tab === t.id ? " active" : "")}
               onClick={() => setTab(t.id)}>
            {t.label}
          </div>
        ))}
      </div>

      {/* ── Active tab. key={tab} replays the .tab-panel fade ── */}
      <div key={tab} className="tab-panel">
        {tab === "resumen"        && <ResumenTab pulse={pulse} reload={reload} goTo={setTab} />}
        {tab === "reconciliacion" && <BotLitisoftReconciliacion />}
        {tab === "cola"           && <BotLitisoftCola onChanged={reload} />}
        {tab === "monitor"        && <BotLitisoftMonitor />}
        {tab === "historial"      && <BotLitisoftHistorial />}
        {tab === "config"         && <BotLitisoftConfig />}
      </div>
    </div>
  );
}

function ReadyChip({ ok, warn, label }) {
  const color = ok ? "var(--color-success)" : warn ? "var(--color-warning)" : "var(--text-hint)";
  const bg = ok ? "var(--color-success-bg)" : warn ? "var(--color-warning-bg)" : "var(--bg-subtle)";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px",
      borderRadius: "var(--r-pill)", fontSize: 11.5, fontWeight: 600, color, background: bg, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
      {label} {ok ? "✓" : warn ? "·" : "—"}
    </span>
  );
}
